import { FC, ReactElement, useEffect, useMemo, useState } from 'react';
import { Field } from '@sitecore-jss/sitecore-jss-nextjs';
import { useRouter } from 'next/router';

import { DISPLAY_VERSIONS } from '../MegaMenu';
import { LanguageSelectorMobile } from './mobile/LanguageSelectorMobile';
import { getCurrentLanguage } from './LanguageSelectorButton';
import { LanguageSelectorDesktop } from './LanguageSelectorDesktop';

export type LanguageType = {
  id: string;
  countryName: Field<string>;
  languageName: Field<string>;
  targetHost: Field<string>;
  targetLocale: Field<string>;
  icon: {
    jsonValue: {
      value: {
        src: string;
      };
    };
  };
  currency: Field<string>;
};

export type RegionType = {
  id: string;
  title: Field<string>;
  titleShared: Field<string>;
  languages: { results: LanguageType[] };
};

export type LanguageSelectorProps = {
  fields: {
    data: {
      item: {
        id: string;
        regions: { results: RegionType[] };
      };
    };
  };
};

export const LanguageSelector: FC<LanguageSelectorProps> = ({ fields }): ReactElement => {
  const defaultRegion = fields?.data?.item?.regions?.results[0];
  const [selectedRegion, setSelectedRegion] = useState<string>(defaultRegion?.id || '');
  const [focusedRegion, setFocusedRegion] = useState<string>(defaultRegion?.id || '');
  const [focusedLanguage, setFocusedLanguage] = useState<string>('');
  const regions = useMemo(
    () => fields?.data?.item?.regions?.results || [],
    [fields?.data?.item?.regions?.results]
  );
  const regionIds = useMemo(() => regions.map((region) => region.id), [regions]);

  useEffect(() => {
    setFocusedRegion(regionIds[0]);
  }, [setFocusedRegion, regionIds]);

  const handleRegionSelect = (id: string) => {
    setSelectedRegion(id);
    setFocusedRegion('');
    setFocusedLanguage(regions.find((r) => r.id === id)?.languages.results[0].id || '');
  };

  const { locale } = useRouter();
  const currentLanguage = getCurrentLanguage(locale, regions);

  return (
    <div className={`absolute flex w-full ${DISPLAY_VERSIONS.DESKTOP}`}>
      <LanguageSelectorMobile regions={regions} handleRegionSelect={handleRegionSelect} />
      <div className="card-shadow w-full bg-white p-8 max-lg:hidden">
        <LanguageSelectorDesktop
          currentLanguage={currentLanguage}
          focusedLanguage={focusedLanguage}
          regions={regions}
          selectedRegion={selectedRegion}
          focusedRegion={focusedRegion}
          handleRegionSelect={handleRegionSelect}
        />
      </div>
    </div>
  );
};
